import React, { useCallback, useEffect, useRef } from 'react';

const W = 360;
const H = 540;
const GROUND_H = 64;
const GRAVITY = 0.12;
const FLAP_FORCE = -4.3;
const PIPE_WIDTH = 52;
const PIPE_CAP_H = 16;
const STEP = 1 / 60;

const SKY_CYCLES = [
  ['#3a8abf', '#87ceeb', '#b3e5fc'],
  ['#1a3a6b', '#2d5a8e', '#7ba7c7'],
  ['#0f1d3d', '#1e3a5f', '#4a6d8c'],
  ['#050b1a', '#0d1f3c', '#1a2d4a'],
  ['#1a0a2e', '#2d1b4e', '#4a2d6b'],
  ['#2d1a0a', '#5c3a1a', '#8c6a3a'],
  ['#5c2a0a', '#a05020', '#d4844a'],
  ['#3a8abf', '#87ceeb', '#b3e5fc'],
];
const STAR_ALPHA = [0, 0.15, 0.55, 1, 1, 0.7, 0.2, 0];

const WEATHER_SEGMENTS = [
  { name: 'clear', frames: 1200 },
  { name: 'breeze', frames: 900 },
  { name: 'rain', frames: 1200 },
  { name: 'storm', frames: 900 },
  { name: 'mist', frames: 1200 },
  { name: 'clear', frames: 1500 },
];
const WEATHER_FADE = 180;
const WEATHER_WIND = { clear: 1, breeze: 2, rain: 1.45, storm: 2.45, mist: 0.55 };

const BIRD_PALETTES = [
  { need: 0, name: 'YELLOW', body1: '#ffde3a', body2: '#ffa800', wing: '#ff8c00' },
  { need: 10, name: 'PINK', body1: '#ff91b8', body2: '#eb3d85', wing: '#ff6199' },
  { need: 20, name: 'BLUE', body1: '#5cc7ff', body2: '#1a66d9', wing: '#2994ff' },
  { need: 30, name: 'GREEN', body1: '#7ae85c', body2: '#1f8f40', wing: '#38b84d' },
  { need: 40, name: 'PURPLE', body1: '#bd94ff', body2: '#6133b8', wing: '#8c54e0' },
  { need: 50, name: 'BLACK', body1: '#2e333d', body2: '#090c0f', wing: '#4d5261' },
];

const C = {
  yellow: '#ffd93d', white: '#ffffff', red: '#ff6b6b', orange: '#ff6b35',
  cloud: '#ffffff', cloudShadow: '#d8eef8',
  ground: '#5d8a3c', groundStripe: '#4a7a2e', dirt: '#8b6355',
  pipeLight: '#70d44a', pipeMid: '#5cbb3c', pipeDark: '#38801e',
  panel: 'rgba(8,12,28,0.88)', green: '#4aa028', greenBorder: '#70d44a',
};

const QUACK_URL = `${process.env.PUBLIC_URL || ''}/assets/duck-quack-quack.wav`;

function safeNumber(primary, fallback, defaultValue = 0) {
  try {
    const raw = localStorage.getItem(primary) ?? (fallback ? localStorage.getItem(fallback) : null);
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function safeLeaderboard() {
  try {
    const raw = localStorage.getItem('pt_lb') ?? localStorage.getItem('fb_lb') ?? '[]';
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((entry) => typeof entry === 'number'
      ? { score: Math.max(0, Math.floor(entry)), date: 0 }
      : { score: Math.max(0, Math.floor(Number(entry?.score) || 0)), date: Number(entry?.date) || 0 })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  } catch {
    return [];
  }
}

function loadFeathers() {
  return Math.max(safeNumber('pt_feathers'), safeNumber('pt_feathers_backup'));
}

function saveFeathers(total) {
  try {
    const value = String(Math.max(0, Math.floor(total)));
    localStorage.setItem('pt_feathers', value);
    localStorage.setItem('pt_feathers_backup', value);
    return loadFeathers() === Number(value);
  } catch {
    return false;
  }
}

function saveHighScore(score) {
  try {
    localStorage.setItem('pt_hi', String(score));
  } catch {}
}

function saveLeaderboard(board) {
  try {
    localStorage.setItem('pt_lb', JSON.stringify(board));
  } catch {}
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
}

function makeStars() {
  const random = seededRandom(0x53544152);
  return Array.from({ length: 55 }, () => ({
    x: random() * W,
    y: random() * H * 0.72,
    radius: random() < 0.15 ? 1.5 : random() < 0.6 ? 1 : 0.7,
    phase: random() * Math.PI * 2,
    speed: 0.04 + random() * 0.04,
  }));
}

function makeWeatherParticles() {
  const random = seededRandom(0x57454154);
  return Array.from({ length: 74 }, () => ({
    x: random() * W,
    y: random() * H,
    speed: 2.8 + random() * 4.8,
    length: 7 + random() * 11,
    alpha: 0.28 + random() * 0.46,
    phase: random() * Math.PI * 2,
  }));
}

function makeClouds() {
  const random = seededRandom(0x434c4f55);
  return Array.from({ length: 6 }, (_, index) => ({
    x: (W / 6) * index + random() * 40,
    y: 20 + random() * H * 0.22,
    w: 36 + random() * 50,
    speed: 0.15 + random() * 0.25,
    layer: index % 2,
  }));
}

function makeBird() {
  return {
    x: W * 0.22, y: H / 2, vy: 0, rot: 0,
    squash: 1, stretch: 1, wingFrame: 0,
    blinkTimer: 90, blinkOpen: true,
  };
}

function makeGameState({ phase = 'menu', totalFeathers, lives = 3 } = {}) {
  return {
    phase, frame: 0, score: 0,
    hiScore: safeNumber('pt_hi', 'fb_hi'),
    leaderboard: safeLeaderboard(),
    totalFeathers: totalFeathers == null ? loadFeathers() : Math.max(0, Math.floor(totalFeathers)),
    lives: Math.max(0, Math.min(3, Math.floor(lives))),
    bird: makeBird(), pipes: [], clouds: makeClouds(), stars: makeStars(),
    weatherParticles: makeWeatherParticles(), particles: [], floatScores: [], floatFeathers: [],
    groundX: 0, shakeMag: 0, shakeX: 0, shakeY: 0,
    deathTimer: 0, menuBob: 0, pipeTimer: 0,
    skyPhase: 0, skyT: 0, weatherClock: 0,
    nextFeatherIn: 2, collectibleRandom: seededRandom(0x46454154),
    audioActive: false, nextQuackFrame: 600 + Math.floor(Math.random() * 720),
  };
}

function midiHz(note) {
  return 440 * (2 ** ((note - 69) / 12));
}

function createAudio() {
  let context = null;
  let musicTimer = null;
  let musicStep = 0;
  let musicState = { phase: 'menu', score: 0, night: 0, storm: 0, rain: 0 };
  const lead = [74,77,81,86,84,81,77,0,70,74,77,82,81,77,74,0,72,77,81,84,81,77,72,0,72,76,79,84,83,79,76,0];
  const chords = [[62,65,69],[58,62,65],[60,65,69],[60,64,67],[62,65,69],[55,58,62],[58,62,65],[57,61,64]];
  const bass = [50,46,53,48,50,43,46,45];

  function getContext() {
    if (!context) context = new (window.AudioContext || window.webkitAudioContext)();
    if (context.state === 'suspended') context.resume().catch(() => {});
    return context;
  }

  function tone(type, frequency, gain, duration, endFrequency, when = 0) {
    const ac = getContext();
    if (ac.state !== 'running') return;
    const start = ac.currentTime + when;
    const oscillator = ac.createOscillator();
    const envelope = ac.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, frequency), start);
    if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), start + duration);
    envelope.gain.setValueAtTime(Math.max(0.0001, gain), start);
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(envelope);
    envelope.connect(ac.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  function scheduleMusicStep() {
    if (!context || context.state !== 'running') return;
    const step = musicStep++ % 128;
    const bar = Math.floor(step / 16) % 8;
    const quiet = musicState.phase === 'dying' || musicState.phase === 'dead';
    const weatherSoftening = musicState.storm * 0.3 + musicState.rain * 0.1;
    const nightSoftening = musicState.night * 0.4;
    const master = (quiet ? 0.22 : musicState.phase === 'menu' ? 0.72 : 0.58)
      * (1 - weatherSoftening) * (1 - nightSoftening * 0.35);
    const tier = musicState.score >= 16 ? 3 : musicState.score >= 8 ? 2 : musicState.score >= 3 ? 1 : 0;
    const chord = chords[bar];
    const arpOrder = [0,1,2,1,0,1,2,1];
    tone('square', midiHz(chord[arpOrder[step % 8]] + 12), 0.018 * master, 0.082);
    if (step % 4 === 0) {
      const offset = [0,12,7,12][Math.floor((step % 16) / 4)];
      tone('triangle', midiHz(bass[bar] + offset), 0.055 * master, 0.34);
    }
    if (step % 2 === 0) {
      const note = lead[Math.floor(step / 2) % lead.length];
      if (note) tone('square', midiHz(note), 0.034 * master, 0.16);
    }
    if (tier > 0 && step % 2 === 1) {
      const sparkle = chord[(Math.floor(step / 2) % 3)] + 24;
      tone('square', midiHz(sparkle), 0.0105 * master * (tier / 3), 0.06);
    }
    if (tier > 1 && step % 4 === 2) tone('sine', 126, 0.018 * master, 0.055, 62);
  }

  function startMusic() {
    getContext();
    if (!musicTimer) {
      scheduleMusicStep();
      musicTimer = window.setInterval(scheduleMusicStep, 100);
    }
  }

  function quack() {
    startMusic();
    const sample = new Audio(QUACK_URL);
    sample.volume = 0.72;
    sample.play().catch(() => {
      tone('sawtooth', 260, 0.055, 0.12, 150);
      tone('square', 205, 0.04, 0.09, 120, 0.16);
    });
  }

  return {
    startMusic,
    update(gs) {
      const weather = weatherAt(gs.weatherClock);
      musicState = {
        phase: gs.phase,
        score: gs.score,
        night: starAlpha(gs),
        storm: weatherAmount(weather, 'storm'),
        rain: weatherAmount(weather, 'rain'),
      };
    },
    flap() { startMusic(); tone('sine', 540, 0.18, 0.09, 370); },
    score() {
      startMusic();
      [660, 880, 1100].forEach((frequency, index) => tone('square', frequency, 0.075, 0.11, null, index * 0.07));
    },
    hit() {
      startMusic();
      tone('sawtooth', 220, 0.22, 0.18, 75);
      tone('sine', 110, 0.13, 0.12, 60, 0.06);
    },
    defeat() {
      startMusic();
      [587,523,440,349,293].forEach((frequency, index) => tone('square', frequency, 0.07, 0.18, null, index * 0.13));
      tone('sawtooth', 220, 0.10, 0.42, 82, 0.72);
    },
    collect() {
      startMusic();
      [880,1175,1568].forEach((frequency, index) => tone(index === 2 ? 'sine' : 'square', frequency, 0.075, 0.15, frequency * 1.1, index * 0.08));
    },
    quack,
    stop() {
      if (musicTimer) window.clearInterval(musicTimer);
      musicTimer = null;
      if (context) context.close().catch(() => {});
      context = null;
    },
  };
}

function birdPalette(total) {
  return BIRD_PALETTES.reduce((selected, palette) => total >= palette.need ? palette : selected, BIRD_PALETTES[0]);
}

function weatherAt(clock) {
  const total = WEATHER_SEGMENTS.reduce((sum, segment) => sum + segment.frames, 0);
  let cursor = clock % total;
  for (let index = 0; index < WEATHER_SEGMENTS.length; index += 1) {
    const segment = WEATHER_SEGMENTS[index];
    if (cursor < segment.frames) {
      return {
        current: segment.name,
        next: WEATHER_SEGMENTS[(index + 1) % WEATHER_SEGMENTS.length].name,
        transition: Math.min(1, Math.max(0, (cursor - (segment.frames - WEATHER_FADE)) / WEATHER_FADE)),
      };
    }
    cursor -= segment.frames;
  }
  return { current: 'clear', next: 'clear', transition: 0 };
}

function weatherAmount(weather, name) {
  return (weather.current === name ? 1 - weather.transition : 0) + (weather.next === name ? weather.transition : 0);
}

function pipeSpeed(score) {
  return 2.2 + score * 0.04;
}

function makePipe(gs) {
  const gap = Math.max(108, 152 - gs.score * 1.8);
  const topH = 50 + Math.random() * (H - GROUND_H - 100 - gap);
  const pipe = { x: W + 16, topH, gap, passed: false };
  gs.nextFeatherIn -= 1;
  if (gs.nextFeatherIn <= 0) {
    const edge = Math.max(25, Math.min(33, gap * 0.22));
    const upper = gs.collectibleRandom() < 0.5;
    pipe.feather = {
      y: topH + (upper ? edge : gap - edge),
      phase: gs.collectibleRandom() * Math.PI * 2,
      collected: false,
    };
    gs.nextFeatherIn = 3 + Math.floor(gs.collectibleRandom() * 3);
  }
  return pipe;
}

function featherPose(gs, pipe) {
  const feather = pipe.feather;
  const drift = gs.frame * 0.052 + feather.phase;
  return {
    x: pipe.x + PIPE_WIDTH / 2 + Math.sin(drift) * 3.8,
    y: feather.y + Math.sin(gs.frame * 0.074 + feather.phase) * 4.5
      + Math.sin(gs.frame * 0.023 + feather.phase * 1.7) * 1.5,
    angle: -0.08 + Math.sin(gs.frame * 0.043 + feather.phase) * 0.25
      + Math.sin(gs.frame * 0.019 + feather.phase * 0.7) * 0.06,
    scale: 0.98 + Math.sin(gs.frame * 0.061 + feather.phase) * 0.035,
  };
}

function spawnParticles(gs, x, y, count, colors = [C.yellow, C.orange, C.white]) {
  for (let index = 0; index < count; index += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1.2 + Math.random() * 3.2;
    gs.particles.push({
      x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 1,
      life: 1, color: colors[index % colors.length], size: 2 + Math.random() * 4,
    });
  }
}

function collectFeather(gs, pipe, audio) {
  if (!pipe.feather || pipe.feather.collected) return;
  const pose = featherPose(gs, pipe);
  const dx = gs.bird.x - pose.x;
  const dy = gs.bird.y - pose.y;
  if (dx * dx + dy * dy > 21 * 21) return;
  pipe.feather.collected = true;
  gs.totalFeathers += 1;
  saveFeathers(gs.totalFeathers);
  audio.collect();
  gs.shakeMag = Math.max(gs.shakeMag, 2.8);
  spawnParticles(gs, pose.x, pose.y, 18, [C.yellow, '#fff78c', C.white]);
  gs.floatFeathers.push({ x: pose.x, y: pose.y - 18, life: 1 });
}

function triggerDeath(gs, audio) {
  if (gs.phase !== 'play') return;
  gs.lives = Math.max(0, gs.lives - 1);
  if (gs.lives === 0) {
    gs.totalFeathers = 0;
    saveFeathers(0);
  }
  audio.hit();
  audio.defeat();
  gs.phase = 'dying';
  gs.deathTimer = 0;
  gs.shakeMag = 11;
  spawnParticles(gs, gs.bird.x, gs.bird.y, 14);
  if (gs.score > 0) {
    gs.leaderboard.push({ score: gs.score, date: Date.now() });
    gs.leaderboard.sort((a, b) => b.score - a.score);
    gs.leaderboard = gs.leaderboard.slice(0, 5);
    saveLeaderboard(gs.leaderboard);
  }
}

function tickParticles(gs) {
  gs.particles.forEach((particle) => {
    particle.x += particle.vx;
    particle.y += particle.vy;
    particle.vy += 0.13;
    particle.life -= 0.032;
  });
  gs.particles = gs.particles.filter((particle) => particle.life > 0);
}

function updateGame(gs, audio) {
  gs.frame += 1;
  gs.menuBob = Math.sin(gs.frame * 0.055) * 5;
  gs.weatherClock += 1;
  const weather = weatherAt(gs.weatherClock);
  const wind = (WEATHER_WIND[weather.current] || 1) * (1 - weather.transition)
    + (WEATHER_WIND[weather.next] || 1) * weather.transition;
  gs.clouds.forEach((cloud) => {
    cloud.x -= cloud.speed * wind;
    if (cloud.x + cloud.w < 0) {
      cloud.x = W + 8;
      cloud.y = 20 + Math.random() * H * 0.22;
      cloud.w = 36 + Math.random() * 50;
    }
  });
  const groundSpeed = gs.phase === 'play' || gs.phase === 'dying' ? pipeSpeed(gs.score) : 0.5;
  gs.groundX = (gs.groundX - groundSpeed) % 40;

  if (gs.audioActive && gs.frame >= gs.nextQuackFrame && ['menu', 'ready', 'play'].includes(gs.phase)) {
    audio.quack();
    gs.nextQuackFrame = gs.frame + 600 + Math.floor(Math.random() * 720);
  }

  if (gs.phase === 'menu') {
    gs.bird.x = W / 2;
    gs.bird.y = 288 + gs.menuBob;
    gs.bird.vy = 0;
    gs.bird.rot = 0;
    gs.bird.wingFrame = Math.floor(gs.frame / 6) % 3;
    audio.update(gs);
    return;
  }
  if (gs.phase === 'ready') {
    gs.bird.x = W * 0.22;
    gs.bird.y = H / 2 + gs.menuBob;
    gs.bird.vy = 0;
    gs.bird.rot = 0;
    gs.bird.wingFrame = Math.floor(gs.frame / 6) % 3;
    audio.update(gs);
    return;
  }
  if (gs.phase === 'dying') {
    gs.deathTimer += 1;
    gs.bird.vy += 0.46 * 1.6;
    gs.bird.y += gs.bird.vy;
    gs.bird.rot = Math.min(90, gs.bird.rot + 9);
    gs.shakeMag *= 0.78;
    gs.shakeX = (Math.random() - 0.5) * gs.shakeMag;
    gs.shakeY = (Math.random() - 0.5) * gs.shakeMag;
    if (gs.deathTimer > 55) gs.phase = 'dead';
    tickParticles(gs);
    audio.update(gs);
    return;
  }
  if (gs.phase === 'dead') {
    audio.update(gs);
    return;
  }

  const bird = gs.bird;
  bird.vy += GRAVITY;
  bird.y += bird.vy;
  const targetRotation = Math.min(90, Math.max(-22, bird.vy * 5.2));
  bird.rot += (targetRotation - bird.rot) * 0.14;
  bird.squash += (1 - bird.squash) * 0.22;
  bird.stretch += (1 - bird.stretch) * 0.22;
  bird.wingFrame = Math.floor(gs.frame / 6) % 3;
  bird.blinkTimer -= 1;
  if (bird.blinkTimer <= 0) {
    bird.blinkOpen = !bird.blinkOpen;
    bird.blinkTimer = bird.blinkOpen ? 80 + Math.random() * 120 : 7;
  }
  gs.shakeMag *= 0.82;
  gs.shakeX = (Math.random() - 0.5) * gs.shakeMag;
  gs.shakeY = (Math.random() - 0.5) * gs.shakeMag;
  if (gs.skyT < 1) gs.skyT = Math.min(1, gs.skyT + 0.016);

  gs.pipeTimer += 1;
  const spawnEvery = Math.max(82, 138 - gs.score * 2);
  if (gs.pipeTimer >= spawnEvery) {
    gs.pipeTimer = 0;
    gs.pipes.push(makePipe(gs));
  }
  const speed = pipeSpeed(gs.score);
  gs.pipes.forEach((pipe) => {
    pipe.x -= speed;
    collectFeather(gs, pipe, audio);
    if (!pipe.passed && pipe.x + PIPE_WIDTH < bird.x) {
      pipe.passed = true;
      gs.score += 1;
      if (gs.score > gs.hiScore) {
        gs.hiScore = gs.score;
        saveHighScore(gs.hiScore);
      }
      audio.score();
      gs.floatScores.push({ x: bird.x + 20, y: bird.y - 22, life: 1 });
      if (gs.score % 10 === 0) {
        gs.skyPhase = (gs.skyPhase + 1) % SKY_CYCLES.length;
        gs.skyT = 0;
      }
    }
  });
  gs.pipes = gs.pipes.filter((pipe) => pipe.x > -PIPE_WIDTH - 20);
  gs.floatScores.forEach((item) => { item.y -= 1.4; item.life -= 0.028; });
  gs.floatScores = gs.floatScores.filter((item) => item.life > 0);
  gs.floatFeathers.forEach((item) => { item.y -= 1.1; item.life -= 0.022; });
  gs.floatFeathers = gs.floatFeathers.filter((item) => item.life > 0);
  tickParticles(gs);

  const bx = bird.x - 11, by = bird.y - 11;
  if (bird.y >= H - GROUND_H - 14 || bird.y <= 12) {
    triggerDeath(gs, audio);
  } else {
    for (const pipe of gs.pipes) {
      if (bx + 22 > pipe.x + 4 && bx < pipe.x + PIPE_WIDTH - 4
        && (by < pipe.topH || by + 22 > pipe.topH + pipe.gap)) {
        triggerDeath(gs, audio);
        break;
      }
    }
  }
  audio.update(gs);
}

function hexToRgb(hex) {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function mixHex(first, second, amount) {
  const a = hexToRgb(first), b = hexToRgb(second);
  const channel = (index) => Math.round(a[index] + (b[index] - a[index]) * amount);
  return `rgb(${channel(0)},${channel(1)},${channel(2)})`;
}

function starAlpha(gs) {
  const current = STAR_ALPHA[gs.skyPhase];
  const next = STAR_ALPHA[(gs.skyPhase + 1) % STAR_ALPHA.length];
  return current + (next - current) * gs.skyT;
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, width, height, radius);
  else {
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
}

function pixelText(ctx, value, x, y, size, color, align = 'center', maxWidth) {
  ctx.save();
  ctx.font = `900 ${size}px 'Press Start 2P', 'Courier New', monospace`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.strokeStyle = 'rgba(0,0,0,0.72)';
  ctx.lineWidth = Math.max(1, size * 0.18);
  ctx.lineJoin = 'round';
  ctx.strokeText(String(value), x, y, maxWidth);
  ctx.fillStyle = color;
  ctx.fillText(String(value), x, y, maxWidth);
  ctx.restore();
}

function drawSky(ctx, gs) {
  const current = SKY_CYCLES[gs.skyPhase];
  const next = SKY_CYCLES[(gs.skyPhase + 1) % SKY_CYCLES.length];
  const gradient = ctx.createLinearGradient(0, 0, 0, H);
  gradient.addColorStop(0, mixHex(current[0], next[0], gs.skyT));
  gradient.addColorStop(0.5, mixHex(current[1], next[1], gs.skyT));
  gradient.addColorStop(1, mixHex(current[2], next[2], gs.skyT));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, W, H);
}

function drawStars(ctx, gs) {
  const alpha = starAlpha(gs);
  if (alpha <= 0.01) return;
  gs.stars.forEach((star) => {
    const twinkle = 0.55 + 0.45 * Math.sin(gs.frame * star.speed + star.phase);
    ctx.globalAlpha = alpha * twinkle;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(Math.floor(star.x), Math.floor(star.y), star.radius, star.radius);
  });
  ctx.globalAlpha = 1;
}

function drawCloud(ctx, cloud, alpha) {
  const height = cloud.w * 0.44;
  ctx.globalAlpha = alpha;
  ctx.fillStyle = C.cloud;
  ctx.beginPath();
  // Move to every lobe before drawing it. Without these moveTo calls Canvas
  // joins the circles with filled straight segments, which look like bars.
  const lobes = [
    [0.18, 0.70, 0.42],
    [0.38, 0.53, 0.58],
    [0.62, 0.38, 0.68],
    [0.78, 0.69, 0.44],
    [0.50, 0.76, 0.50],
  ];
  lobes.forEach(([x, y, radius]) => {
    const cx = cloud.x + cloud.w * x;
    const cy = cloud.y + height * y;
    const r = height * radius;
    ctx.moveTo(cx + r, cy);
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
  });
  ctx.fill();
  ctx.globalAlpha = 1;
}

function drawWeatherBackground(ctx, gs) {
  const weather = weatherAt(gs.weatherClock);
  const mist = weatherAmount(weather, 'mist');
  const storm = weatherAmount(weather, 'storm');
  if (mist > 0.01) {
    ctx.fillStyle = `rgba(215,230,235,${0.16 * mist})`;
    ctx.fillRect(0, 0, W, H - GROUND_H);
    for (let index = 0; index < 5; index += 1) {
      const x = ((index * 97 + gs.frame * 0.14) % (W + 160)) - 80;
      ctx.fillStyle = `rgba(240,248,250,${0.055 * mist})`;
      ctx.beginPath();
      ctx.ellipse(x, 120 + index * 62, 90, 24, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (storm > 0.35 && gs.weatherClock % 420 < 4) {
    ctx.fillStyle = `rgba(235,245,255,${0.4 * storm})`;
    ctx.fillRect(0, 0, W, H - GROUND_H);
  }
}

function drawWeatherForeground(ctx, gs) {
  const weather = weatherAt(gs.weatherClock);
  const rain = weatherAmount(weather, 'rain');
  const storm = weatherAmount(weather, 'storm');
  const breeze = weatherAmount(weather, 'breeze');
  const rainAmount = Math.min(1, rain + storm);
  if (rainAmount > 0.01) {
    ctx.lineWidth = storm > 0.2 ? 1.4 : 1;
    gs.weatherParticles.forEach((particle, index) => {
      if (index / gs.weatherParticles.length > rainAmount) return;
      const x = (particle.x - gs.frame * (1.1 + storm * 1.4)) % (W + 30);
      const y = (particle.y + gs.frame * particle.speed) % (H - GROUND_H);
      ctx.strokeStyle = `rgba(205,232,255,${particle.alpha * (0.5 + storm * 0.5)})`;
      ctx.beginPath();
      ctx.moveTo(x < -20 ? x + W + 30 : x, y);
      ctx.lineTo((x < -20 ? x + W + 30 : x) - 4 - storm * 3, y + particle.length);
      ctx.stroke();
    });
  }
  if (breeze > 0.08) {
    for (let index = 0; index < 8; index += 1) {
      const x = ((index * 61 + gs.frame * 1.6) % (W + 60)) - 30;
      const y = 55 + ((index * 73) % 330) + Math.sin(gs.frame * 0.04 + index) * 9;
      ctx.strokeStyle = `rgba(255,255,255,${0.12 * breeze})`;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 17, y - 2);
      ctx.stroke();
    }
  }
}

function drawPipe(ctx, pipe) {
  const capX = pipe.x - 5, capWidth = PIPE_WIDTH + 10;
  const gradient = (x, width) => {
    const fill = ctx.createLinearGradient(x, 0, x + width, 0);
    fill.addColorStop(0, C.pipeDark);
    fill.addColorStop(0.2, C.pipeLight);
    fill.addColorStop(0.78, C.pipeMid);
    fill.addColorStop(1, C.pipeDark);
    return fill;
  };
  ctx.fillStyle = gradient(pipe.x, PIPE_WIDTH);
  ctx.fillRect(pipe.x, 0, PIPE_WIDTH, pipe.topH - PIPE_CAP_H);
  ctx.fillStyle = gradient(capX, capWidth);
  ctx.fillRect(capX, pipe.topH - PIPE_CAP_H, capWidth, PIPE_CAP_H);
  const bottomY = pipe.topH + pipe.gap;
  ctx.fillStyle = gradient(pipe.x, PIPE_WIDTH);
  ctx.fillRect(pipe.x, bottomY + PIPE_CAP_H, PIPE_WIDTH, H - bottomY - PIPE_CAP_H);
  ctx.fillStyle = gradient(capX, capWidth);
  ctx.fillRect(capX, bottomY, capWidth, PIPE_CAP_H);
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.fillRect(pipe.x + 5, 0, 6, Math.max(0, pipe.topH - PIPE_CAP_H));
  ctx.fillRect(pipe.x + 5, bottomY + PIPE_CAP_H, 6, H - bottomY - PIPE_CAP_H);
}

function drawGround(ctx, offset) {
  const y = H - GROUND_H;
  ctx.fillStyle = C.ground;
  ctx.fillRect(0, y, W, GROUND_H);
  ctx.fillStyle = C.dirt;
  ctx.fillRect(0, y + 18, W, GROUND_H - 18);
  ctx.fillStyle = C.groundStripe;
  for (let index = 0; index < W + 44; index += 22) ctx.fillRect(((index + offset) % W) - 4, y, 11, 18);
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.fillRect(0, y, W, 2);
  ctx.fillStyle = 'rgba(0,0,0,0.2)';
  ctx.fillRect(0, y + 18, W, 2);
}

function drawFeatherShape(ctx, x, y, scale = 1, angle = 0, alpha = 1) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(scale, scale);
  ctx.globalAlpha = alpha;
  const glow = ctx.createRadialGradient(0, 0, 2, 0, 0, 18);
  glow.addColorStop(0, 'rgba(255,239,105,0.38)');
  glow.addColorStop(1, 'rgba(255,239,105,0)');
  ctx.fillStyle = glow;
  ctx.beginPath(); ctx.arc(0, 0, 18, 0, Math.PI * 2); ctx.fill();
  const fill = ctx.createLinearGradient(-7, -13, 8, 13);
  fill.addColorStop(0, '#fff48b'); fill.addColorStop(0.5, '#ffd93d'); fill.addColorStop(1, '#e79616');
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(-2, 13);
  ctx.bezierCurveTo(-12, 4, -11, -8, 4, -15);
  ctx.bezierCurveTo(12, -6, 8, 7, -2, 13);
  ctx.fill();
  ctx.strokeStyle = '#a75c0a'; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.moveTo(-3, 15); ctx.lineTo(4, -13); ctx.stroke();
  ctx.lineWidth = 0.9;
  for (let index = 0; index < 5; index += 1) {
    const yy = -8 + index * 4;
    const centerX = 2.7 - (yy + 13) * 0.2;
    ctx.beginPath(); ctx.moveTo(centerX, yy); ctx.lineTo(-7 + index * 0.6, yy - 3); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(centerX, yy); ctx.lineTo(8 - index * 0.5, yy - 4); ctx.stroke();
  }
  ctx.restore();
}

function drawCollectible(ctx, gs, pipe) {
  if (!pipe.feather || pipe.feather.collected) return;
  const pose = featherPose(gs, pipe);
  drawFeatherShape(ctx, pose.x, pose.y, pose.scale, pose.angle, 1);
}

function drawBird(ctx, gs) {
  const bird = gs.bird;
  const palette = birdPalette(gs.totalFeathers);
  ctx.save();
  ctx.translate(Math.floor(bird.x), Math.floor(bird.y));
  ctx.rotate((bird.rot * Math.PI) / 180);
  ctx.scale(bird.stretch, bird.squash);
  ctx.fillStyle = palette.body2; roundRect(ctx, -14, -14, 28, 28, 7); ctx.fill();
  ctx.fillStyle = palette.body1; roundRect(ctx, -12, -12, 24, 24, 6); ctx.fill();
  ctx.save(); ctx.translate(-3, -4); ctx.rotate(-0.3);
  ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.beginPath(); ctx.ellipse(0, 0, 6, 4.5, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
  const wingY = [3, 5, 7][Math.min(bird.wingFrame, 2)];
  ctx.fillStyle = palette.wing; roundRect(ctx, -15, wingY, 10, 7, 3); ctx.fill();
  ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(5, -4, 5, 0, Math.PI * 2); ctx.fill();
  if (bird.blinkOpen) {
    ctx.fillStyle = '#1a1a2e'; ctx.beginPath(); ctx.arc(6, -4, 3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(7, -5.2, 1, 0, Math.PI * 2); ctx.fill();
  } else {
    ctx.fillStyle = palette.body2; ctx.fillRect(1, -7, 9, 5);
    ctx.fillStyle = '#1a1a2e'; ctx.fillRect(2, -6, 7, 2);
  }
  ctx.fillStyle = C.orange; ctx.beginPath(); ctx.moveTo(13, -3.5); ctx.lineTo(23, 0.5); ctx.lineTo(13, 4.5); ctx.closePath(); ctx.fill();
  ctx.restore();
}

function drawPixelHeart(ctx, x, y, active) {
  const block = 2;
  const shape = (dx, dy) => {
    ctx.fillRect(dx + block, dy, block * 2, block);
    ctx.fillRect(dx + block * 4, dy, block * 2, block);
    ctx.fillRect(dx, dy + block, block * 7, block * 2);
    ctx.fillRect(dx + block, dy + block * 3, block * 5, block);
    ctx.fillRect(dx + block * 2, dy + block * 4, block * 3, block);
    ctx.fillRect(dx + block * 3, dy + block * 5, block, block);
  };
  ctx.fillStyle = active ? 'rgba(72,8,25,0.9)' : 'rgba(32,18,25,0.45)'; shape(x + 1, y + 1);
  ctx.fillStyle = active ? '#f52945' : 'rgba(48,26,36,0.48)'; shape(x, y);
  if (active) { ctx.fillStyle = '#ff929d'; ctx.fillRect(x + 4, y + 2, 2, 2); }
}

function drawHud(ctx, gs) {
  ctx.fillStyle = 'rgba(0,0,0,0.30)'; roundRect(ctx, W / 2 - 32, 8, 64, 36, 8); ctx.fill();
  pixelText(ctx, gs.score, W / 2, 26, 22, C.white);
  drawFeatherShape(ctx, 17, 26, 0.58, -0.12, 1);
  pixelText(ctx, gs.totalFeathers, 33, 26, 10, C.yellow, 'left', 38);
  for (let index = 0; index < 3; index += 1) drawPixelHeart(ctx, 76 + index * 18, 20, index < gs.lives);
}

function drawMenu(ctx, gs) {
  const palette = birdPalette(gs.totalFeathers);
  ctx.fillStyle = 'rgba(4,10,25,0.83)'; roundRect(ctx, 28, 18, 304, 86, 13); ctx.fill();
  ctx.strokeStyle = 'rgba(255,217,61,0.52)'; ctx.lineWidth = 2; roundRect(ctx, 28, 18, 304, 86, 13); ctx.stroke();
  pixelText(ctx, 'PECKER TAP', W / 2, 53, 25, C.yellow, 'center', 280);
  pixelText(ctx, 'SKYBOUND ARCADE', W / 2, 83, 7, '#ffc84a', 'center', 260);

  ctx.fillStyle = 'rgba(4,10,25,0.77)'; roundRect(ctx, 40, 116, 280, 78, 11); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.11)'; ctx.lineWidth = 1; roundRect(ctx, 40, 116, 280, 78, 11); ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.fillRect(W / 2, 127, 1, 56);
  pixelText(ctx, 'HIGH SCORE', 110, 135, 7, '#d1e0f4', 'center', 118);
  pixelText(ctx, gs.hiScore, 110, 164, 20, C.yellow);
  pixelText(ctx, 'FEATHERS', 250, 135, 7, '#d1e0f4', 'center', 118);
  pixelText(ctx, gs.totalFeathers, 250, 157, 20, C.yellow);
  pixelText(ctx, `${palette.name} BIRD`, 250, 181, 6.5, '#ffbd57', 'center', 120);

  const pulse = 0.78 + (0.5 + 0.5 * Math.sin(gs.frame * 0.1)) * 0.22;
  ctx.globalAlpha = pulse;
  ctx.fillStyle = 'rgba(10,53,25,0.88)'; roundRect(ctx, 68, 208, 224, 50, 11); ctx.fill();
  ctx.strokeStyle = '#8cff52'; ctx.lineWidth = 2; roundRect(ctx, 68, 208, 224, 50, 11); ctx.stroke();
  pixelText(ctx, 'TAP TO START', W / 2, 233, 14, C.white, 'center', 202);
  ctx.globalAlpha = 1;

  const rows = Math.max(1, Math.min(gs.leaderboard.length, 5));
  const panelHeight = 44 + rows * 19;
  const panelY = 330;
  ctx.fillStyle = 'rgba(4,10,25,0.70)'; roundRect(ctx, 55, panelY, 250, panelHeight, 11); ctx.fill();
  ctx.strokeStyle = 'rgba(255,217,61,0.32)'; roundRect(ctx, 55, panelY, 250, panelHeight, 11); ctx.stroke();
  pixelText(ctx, 'TOP SCORES', W / 2, panelY + 19, 9, C.yellow);
  ctx.fillStyle = 'rgba(255,255,255,0.10)'; ctx.fillRect(69, panelY + 34, 222, 1);
  if (gs.leaderboard.length) {
    const medals = ['#ffd93d', '#c0c0c0', '#cd7f32', '#aaaaaa', '#aaaaaa'];
    gs.leaderboard.slice(0, 5).forEach((entry, index) => {
      const y = panelY + 46 + index * 19;
      pixelText(ctx, `#${index + 1}`, 76, y, 8, medals[index], 'left', 44);
      pixelText(ctx, entry.score, 282, y, 9, C.white, 'right', 70);
    });
  } else if (gs.hiScore > 0) pixelText(ctx, `BEST  ${gs.hiScore}`, W / 2, panelY + 53, 9, C.yellow);
  else pixelText(ctx, 'NO SCORES YET', W / 2, panelY + 53, 7, 'rgba(255,255,255,0.52)');
}

function drawDead(ctx, gs) {
  if (gs.phase === 'dying' && gs.deathTimer <= 20) return;
  const y = H * 0.2;
  ctx.fillStyle = 'rgba(0,0,0,0.52)'; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = C.panel; roundRect(ctx, W / 2 - 100, y, 200, 176, 14); ctx.fill();
  ctx.strokeStyle = C.yellow; ctx.lineWidth = 2.5; roundRect(ctx, W / 2 - 100, y, 200, 176, 14); ctx.stroke();
  pixelText(ctx, 'GAME OVER', W / 2, y + 30, 18, C.red);
  ctx.fillStyle = 'rgba(255,255,255,0.08)'; ctx.fillRect(W / 2 - 80, y + 48, 160, 1);
  pixelText(ctx, 'SCORE', W / 2 - 46, y + 64, 8, '#999');
  pixelText(ctx, 'BEST', W / 2 + 50, y + 64, 8, '#999');
  pixelText(ctx, gs.score, W / 2 - 46, y + 92, 22, C.white);
  const isHigh = gs.score > 0 && gs.score >= gs.hiScore;
  pixelText(ctx, gs.hiScore, W / 2 + 50, y + 92, 22, isHigh ? C.yellow : C.white);
  if (isHigh) pixelText(ctx, 'NEW!', W / 2 + 50, y + 111, 7, C.yellow);
  if (gs.phase === 'dead') {
    ctx.fillStyle = C.green; roundRect(ctx, W / 2 - 70, y + 126, 140, 40, 10); ctx.fill();
    ctx.strokeStyle = C.greenBorder; ctx.lineWidth = 2; roundRect(ctx, W / 2 - 70, y + 126, 140, 40, 10); ctx.stroke();
    pixelText(ctx, 'PLAY AGAIN', W / 2, y + 146, 11, C.white, 'center', 125);
  }
}

function renderGame(ctx, gs) {
  ctx.save();
  ctx.translate(Math.floor(gs.shakeX), Math.floor(gs.shakeY));
  drawSky(ctx, gs);
  drawStars(ctx, gs);
  const cloudVisibility = Math.max(0, 1 - starAlpha(gs) * 1.4);
  gs.clouds.forEach((cloud) => drawCloud(ctx, cloud, (cloud.layer === 0 ? 0.52 : 0.9) * cloudVisibility));
  drawWeatherBackground(ctx, gs);
  gs.pipes.forEach((pipe) => { drawPipe(ctx, pipe); drawCollectible(ctx, gs, pipe); });
  drawGround(ctx, gs.groundX);
  gs.particles.forEach((particle) => {
    ctx.globalAlpha = Math.max(0, particle.life);
    ctx.fillStyle = particle.color;
    roundRect(ctx, particle.x - particle.size / 2, particle.y - particle.size / 2, particle.size, particle.size, 2);
    ctx.fill();
  });
  ctx.globalAlpha = 1;
  if (gs.phase !== 'dead') drawBird(ctx, gs);
  gs.floatScores.forEach((item) => {
    ctx.globalAlpha = Math.max(0, item.life); pixelText(ctx, '+1', item.x, item.y, 14, C.yellow); ctx.globalAlpha = 1;
  });
  gs.floatFeathers.forEach((item) => {
    ctx.globalAlpha = Math.max(0, item.life); pixelText(ctx, 'FEATHER!', item.x, item.y, 7, '#ffe647'); ctx.globalAlpha = 1;
  });
  drawWeatherForeground(ctx, gs);
  ctx.restore();

  if (gs.phase === 'menu') drawMenu(ctx, gs);
  else if (gs.phase === 'ready') { drawHud(ctx, gs); pixelText(ctx, 'GET READY!', W / 2, 80, 13, C.white); }
  else if (gs.phase === 'play') drawHud(ctx, gs);
  else { drawHud(ctx, gs); drawDead(ctx, gs); }

  if (window.electronAPI && gs.phase === 'menu') {
    ctx.fillStyle = 'rgba(255,255,255,0.13)'; roundRect(ctx, W - 34, 7, 26, 26, 6); ctx.fill();
    pixelText(ctx, '×', W - 21, 20, 15, C.white);
  }
}

export default function PeckerTapGame({
  width = 360,
  height = 540,
  interactive = true,
  autoMusic = false,
  borderRadius = 12,
  style = {},
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const audioRef = useRef(null);
  const frameRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    // Above 2x the extra backing-store pixels are hard to see in pixel art but
    // expensive to redraw, especially on phones and high-DPI external screens.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    stateRef.current = makeGameState();
    audioRef.current = createAudio();
    if (autoMusic) audioRef.current.startMusic();
    let previous = performance.now();
    let accumulator = 0;
    let isIntersecting = true;
    let pageVisible = !document.hidden;
    const resetClock = () => {
      previous = performance.now();
      accumulator = 0;
    };
    const visibilityObserver = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(([entry]) => {
        isIntersecting = Boolean(entry?.isIntersecting);
        resetClock();
        if (isIntersecting && pageVisible) renderGame(ctx, stateRef.current);
      }, { rootMargin: '80px', threshold: 0 })
      : null;
    visibilityObserver?.observe(canvas);
    const onVisibilityChange = () => {
      pageVisible = !document.hidden;
      resetClock();
      if (pageVisible && isIntersecting) renderGame(ctx, stateRef.current);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    renderGame(ctx, stateRef.current);
    const loop = (now) => {
      frameRef.current = requestAnimationFrame(loop);
      if (!pageVisible || !isIntersecting) {
        previous = now;
        accumulator = 0;
        return;
      }
      accumulator = Math.min(accumulator + (now - previous) / 1000, STEP * 5);
      previous = now;
      let advanced = false;
      while (accumulator >= STEP) {
        updateGame(stateRef.current, audioRef.current);
        accumulator -= STEP;
        advanced = true;
      }
      // ProMotion/120 Hz displays call rAF twice as often as the 60 Hz game
      // simulation. Only paint after a simulation step to avoid duplicate work.
      if (advanced) renderGame(ctx, stateRef.current);
    };
    frameRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frameRef.current);
      visibilityObserver?.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      audioRef.current?.stop();
    };
  }, [autoMusic]);

  const act = useCallback((clientX, clientY) => {
    if (!interactive || !stateRef.current || !canvasRef.current) return;
    activeRef.current = true;
    const gs = stateRef.current;
    const audio = audioRef.current;
    gs.audioActive = true;
    audio.startMusic();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (clientX - rect.left) * (W / rect.width);
    const y = (clientY - rect.top) * (H / rect.height);
    if (window.electronAPI && gs.phase === 'menu' && x > W - 38 && y < 38) {
      window.electronAPI.closeWindow();
      return;
    }
    if (gs.phase === 'dead') {
      const total = gs.totalFeathers;
      const nextLives = gs.lives > 0 ? gs.lives : 3;
      stateRef.current = makeGameState({ phase: 'menu', totalFeathers: total, lives: nextLives });
      stateRef.current.audioActive = true;
      saveFeathers(total);
      return;
    }
    if (gs.phase === 'menu') { gs.phase = 'ready'; return; }
    if (gs.phase === 'ready') gs.phase = 'play';
    if (gs.phase !== 'play') return;
    gs.bird.vy = FLAP_FORCE;
    gs.bird.squash = 0.68;
    gs.bird.stretch = 1.32;
    audio.flap();
  }, [interactive]);

  useEffect(() => {
    if (!interactive) return undefined;
    const onKey = (event) => {
      if (!activeRef.current && !window.electronAPI) return;
      if (event.code === 'Escape' && window.electronAPI) {
        window.electronAPI.closeWindow();
        return;
      }
      if (!['Space', 'ArrowUp', 'KeyW', 'Enter'].includes(event.code)) return;
      event.preventDefault();
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) act(rect.left + rect.width / 2, rect.top + rect.height / 2);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act, interactive]);

  return (
    <div style={{
      width, height, maxWidth: '100%', overflow: 'hidden', borderRadius,
      background: '#0d0f1a', cursor: interactive ? 'pointer' : 'default',
      userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none', ...style,
    }}>
      <canvas
        ref={canvasRef}
        aria-label="Pecker Tap game"
        onPointerDown={(event) => {
          event.preventDefault();
          act(event.clientX, event.clientY);
        }}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />
    </div>
  );
}
